import { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Plus, ClipboardList, Download, Trash2, Send, Check, X, Play,
  FileText, DollarSign, ArrowRight,
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { toast } from 'sonner';
import {
  getWorkOrders, createWorkOrder, updateWorkOrder,
  deleteWorkOrder, convertWorkOrderToInvoice, getWorkers,
} from '@/lib/api';
import { generateWorkOrderPDF } from '@/lib/workOrderPdf';
import { PhotoUploader } from './PhotoUploader';

const STATUS_COLOR = {
  draft: 'bg-slate-100 text-slate-700 border-slate-200',
  sent: 'bg-blue-50 text-blue-700 border-blue-200',
  accepted: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  rejected: 'bg-red-50 text-red-700 border-red-200',
  in_progress: 'bg-amber-50 text-amber-700 border-amber-200',
  completed: 'bg-emerald-100 text-emerald-800 border-emerald-300',
  converted: 'bg-purple-50 text-purple-700 border-purple-200',
  cancelled: 'bg-slate-50 text-slate-500 border-slate-200',
};

const emptyForm = () => ({
  property_id: '',
  client_name: '',
  client_email: '',
  worker_id: '',
  scope_of_work: '',
  expected_duration_days: '',
  scheduled_start: '',
  scheduled_end: '',
  tax_rate: 0,
  labor_adjustment: 0,
  cost_range_min: 0,
  cost_range_max: 0,
  terms: '',
  notes: '',
  custom_message: '',
  photos: [],
  line_items: [
    { _key: `li-${Date.now()}-0`, description: '', item_type: 'labor', quantity: 1, unit: 'hours', unit_price: 0, amount: 0 },
  ],
});

const newLineItem = () => ({
  _key: `li-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
  description: '',
  item_type: 'labor',
  quantity: 1,
  unit: 'hours',
  unit_price: 0,
  amount: 0,
});

const LineRow = ({ item, idx, onChange, onRemove }) => {
  const upd = (key, val) => {
    const patch = { ...item, [key]: val };
    const qty = Number(patch.quantity) || 0;
    const price = Number(patch.unit_price) || 0;
    patch.amount = Math.round(qty * price * 100) / 100;
    onChange(idx, patch);
  };
  return (
    <div className="grid grid-cols-12 gap-2 items-center" data-testid={`line-row-${idx}`}>
      <Select value={item.item_type} onValueChange={(v) => upd('item_type', v)}>
        <SelectTrigger className="col-span-2 h-9 text-xs"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="labor">Labor</SelectItem>
          <SelectItem value="material">Material</SelectItem>
        </SelectContent>
      </Select>
      <Input
        placeholder="Description"
        value={item.description}
        onChange={(e) => upd('description', e.target.value)}
        className="col-span-4 h-9 text-sm"
      />
      <Input
        type="number" min="0" step="0.25"
        placeholder="Qty"
        value={item.quantity}
        onChange={(e) => upd('quantity', e.target.value)}
        className="col-span-1 h-9 text-sm"
      />
      <Input
        placeholder="Unit"
        value={item.unit}
        onChange={(e) => upd('unit', e.target.value)}
        className="col-span-1 h-9 text-sm"
      />
      <Input
        type="number" min="0" step="0.01"
        placeholder="Rate"
        value={item.unit_price}
        onChange={(e) => upd('unit_price', e.target.value)}
        className="col-span-2 h-9 text-sm"
      />
      <div className="col-span-1 text-sm font-medium text-slate-900 text-right">
        ${(item.amount || 0).toFixed(2)}
      </div>
      <Button
        type="button" variant="ghost" size="sm" className="col-span-1 text-red-500"
        onClick={() => onRemove(idx)}
      >
        <Trash2 className="h-3 w-3" />
      </Button>
    </div>
  );
};

export const WorkOrdersView = ({ selectedProperty, properties }) => {
  const [orders, setOrders] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [busy, setBusy] = useState(false);

  const fetchOrders = useCallback(async () => {
    try {
      setLoading(true);
      const params = selectedProperty?.id ? { property_id: selectedProperty.id } : {};
      const { data } = await getWorkOrders(params);
      setOrders(data);
    } catch (_) {
      toast.error('Failed to load work orders');
    } finally {
      setLoading(false);
    }
  }, [selectedProperty]);

  useEffect(() => {
    fetchOrders();
    getWorkers(true).then((r) => setWorkers(r.data)).catch(() => {});
  }, [fetchOrders]);

  const openCreate = () => {
    setEditing(null);
    setForm({ ...emptyForm(), property_id: selectedProperty?.id || properties?.[0]?.id || '' });
    setDialogOpen(true);
  };

  const openEdit = (wo) => {
    setEditing(wo);
    setForm({
      property_id: wo.property_id,
      client_name: wo.client_name || '',
      client_email: wo.client_email || '',
      worker_id: wo.worker_id || '',
      scope_of_work: wo.scope_of_work || '',
      expected_duration_days: wo.expected_duration_days || '',
      scheduled_start: wo.scheduled_start || '',
      scheduled_end: wo.scheduled_end || '',
      tax_rate: wo.tax_rate || 0,
      labor_adjustment: wo.labor_adjustment || 0,
      cost_range_min: wo.cost_range_min || 0,
      cost_range_max: wo.cost_range_max || 0,
      terms: wo.terms || '',
      notes: wo.notes || '',
      custom_message: wo.custom_message || '',
      photos: wo.photos || [],
      line_items: wo.line_items?.length
        ? wo.line_items.map((li, idx) => ({ ...li, _key: li._key || `li-server-${wo.id}-${idx}` }))
        : emptyForm().line_items,
    });
    setDialogOpen(true);
  };

  const addLine = (type = 'labor') =>
    setForm((p) => ({
      ...p,
      line_items: [
        ...p.line_items,
        { ...newLineItem(), item_type: type, unit: type === 'labor' ? 'hours' : 'unit' },
      ],
    }));

  const updateLine = (i, val) =>
    setForm((p) => ({ ...p, line_items: p.line_items.map((li, idx) => (idx === i ? val : li)) }));

  const removeLine = (i) =>
    setForm((p) => ({ ...p, line_items: p.line_items.filter((_, idx) => idx !== i) }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.property_id) return toast.error('Select a property');
    if (!form.scope_of_work?.trim()) return toast.error('Scope of work is required');
    setBusy(true);
    try {
      const payload = {
        ...form,
        expected_duration_days: form.expected_duration_days ? parseInt(form.expected_duration_days) : null,
        tax_rate: Number(form.tax_rate) || 0,
        labor_adjustment: Number(form.labor_adjustment) || 0,
        cost_range_min: Number(form.cost_range_min) || 0,
        cost_range_max: Number(form.cost_range_max) || 0,
        line_items: form.line_items
          .filter((li) => li.description?.trim())
          .map((li) => ({
            description: li.description,
            item_type: li.item_type,
            quantity: Number(li.quantity) || 0,
            unit: li.unit || '',
            unit_price: Number(li.unit_price) || 0,
            amount: Number(li.amount) || 0,
          })),
      };
      if (editing) {
        await updateWorkOrder(editing.id, payload);
        toast.success('Work order updated');
      } else {
        await createWorkOrder(payload);
        toast.success('Work order created');
      }
      setDialogOpen(false);
      fetchOrders();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  const transition = async (wo, next) => {
    try {
      await updateWorkOrder(wo.id, { status: next });
      toast.success(`Marked ${next.replace('_', ' ')}`);
      fetchOrders();
    } catch (_) {
      toast.error('Failed to update status');
    }
  };

  const remove = async (wo) => {
    if (!window.confirm(`Delete work order ${wo.work_order_number}?`)) return;
    try {
      await deleteWorkOrder(wo.id);
      toast.success('Deleted');
      fetchOrders();
    } catch (_) {
      toast.error('Delete failed');
    }
  };

  const convert = async (wo) => {
    if (!window.confirm(`Convert ${wo.work_order_number} to invoice?`)) return;
    try {
      const { data } = await convertWorkOrderToInvoice(wo.id);
      toast.success(`Created invoice ${data.invoice_number}`);
      fetchOrders();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Convert failed');
    }
  };

  const download = (wo) => {
    try { generateWorkOrderPDF(wo); toast.success('PDF downloaded'); }
    catch (err) { console.error(err); toast.error('PDF generation failed'); }
  };

  const stats = useMemo(() => ({
    total: orders.length,
    open: orders.filter((o) => ['draft','sent','accepted','in_progress'].includes(o.status)).length,
    completed: orders.filter((o) => o.status === 'completed').length,
    totalValue: orders.reduce((s, o) => s + (o.total || 0), 0),
  }), [orders]);

  return (
    <div className="space-y-6" data-testid="work-orders-view">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-manrope font-bold text-3xl text-slate-900 tracking-tight">Work Orders</h1>
          <p className="text-slate-500 mt-1">
            {selectedProperty ? selectedProperty.name : 'All Properties'} — quotes &amp; scheduled work
          </p>
        </div>
        <Button onClick={openCreate} className="bg-slate-900 hover:bg-slate-800" data-testid="new-work-order-btn">
          <Plus className="h-4 w-4 mr-2" /> New Work Order
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { icon: ClipboardList, label: 'Total', value: stats.total, bg: 'bg-slate-100', color: 'text-slate-700' },
          { icon: Play, label: 'Open', value: stats.open, bg: 'bg-amber-100', color: 'text-amber-700' },
          { icon: Check, label: 'Completed', value: stats.completed, bg: 'bg-emerald-100', color: 'text-emerald-700' },
          { icon: DollarSign, label: 'Total Value', value: `$${stats.totalValue.toLocaleString()}`, bg: 'bg-blue-100', color: 'text-blue-700' },
        ].map((s) => (
          <Card key={s.label} className="bg-white border border-slate-100">
            <CardContent className="p-4 flex items-center gap-3">
              <div className={`w-10 h-10 ${s.bg} rounded-lg flex items-center justify-center`}>
                <s.icon className={`h-5 w-5 ${s.color}`} />
              </div>
              <div>
                <p className="text-sm text-slate-500">{s.label}</p>
                <p className="text-xl font-bold text-slate-900">{s.value}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64"><div className="spinner"></div></div>
      ) : orders.length === 0 ? (
        <Card className="bg-white border border-slate-100">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <ClipboardList className="h-16 w-16 text-slate-300 mb-4" />
            <h3 className="text-lg font-semibold mb-2">No work orders yet</h3>
            <p className="text-slate-500 mb-4">Send a quote to a client before the work starts</p>
            <Button onClick={openCreate} data-testid="first-work-order-btn">
              <Plus className="h-4 w-4 mr-2" /> New Work Order
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {orders.map((wo) => (
            <Card key={wo.id} className="bg-white border border-slate-100" data-testid={`wo-card-${wo.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="font-mono text-sm text-slate-500">{wo.work_order_number}</span>
                      <span className={`px-2 py-0.5 rounded text-xs font-medium border ${STATUS_COLOR[wo.status]}`}>
                        {wo.status.replace('_', ' ')}
                      </span>
                      <span className="text-sm text-slate-700 font-medium">
                        {wo.client_name || wo.property_name}
                      </span>
                      <span className="text-xs text-slate-400">· {wo.property_name}</span>
                    </div>
                    {wo.scope_of_work && (
                      <p className="text-sm text-slate-600 line-clamp-1">{wo.scope_of_work}</p>
                    )}
                    <div className="flex items-center gap-3 text-xs text-slate-500 mt-1 flex-wrap">
                      {wo.scheduled_start && (
                        <span>Start: {format(parseISO(wo.scheduled_start), 'MMM d')}</span>
                      )}
                      {wo.expected_duration_days && (
                        <span>~{wo.expected_duration_days} day{wo.expected_duration_days === 1 ? '' : 's'}</span>
                      )}
                      {wo.worker_name && <span>Worker: {wo.worker_name}</span>}
                      <span className="font-semibold text-emerald-600">${(wo.total || 0).toFixed(2)}</span>
                    </div>
                  </div>

                  <div className="flex items-center gap-1 flex-wrap">
                    {wo.status === 'draft' && (
                      <Button size="sm" variant="outline" onClick={() => transition(wo, 'sent')} data-testid={`send-${wo.id}`}>
                        <Send className="h-3 w-3 mr-1" /> Send
                      </Button>
                    )}
                    {wo.status === 'sent' && (
                      <>
                        <Button size="sm" variant="outline" onClick={() => transition(wo, 'accepted')} data-testid={`accept-${wo.id}`}>
                          <Check className="h-3 w-3 mr-1 text-emerald-600" /> Accepted
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => transition(wo, 'rejected')} data-testid={`reject-${wo.id}`}>
                          <X className="h-3 w-3 mr-1 text-red-600" /> Rejected
                        </Button>
                      </>
                    )}
                    {wo.status === 'accepted' && (
                      <Button size="sm" variant="outline" onClick={() => transition(wo, 'in_progress')} data-testid={`start-${wo.id}`}>
                        <Play className="h-3 w-3 mr-1" /> Start
                      </Button>
                    )}
                    {wo.status === 'in_progress' && (
                      <Button size="sm" variant="outline" onClick={() => transition(wo, 'completed')} data-testid={`complete-${wo.id}`}>
                        <Check className="h-3 w-3 mr-1 text-emerald-600" /> Complete
                      </Button>
                    )}
                    {wo.status === 'completed' && !wo.invoice_id && (
                      <Button size="sm" variant="outline" onClick={() => convert(wo)} data-testid={`convert-${wo.id}`}>
                        <ArrowRight className="h-3 w-3 mr-1" /> To Invoice
                      </Button>
                    )}
                    <Button variant="ghost" size="sm" onClick={() => download(wo)} data-testid={`pdf-${wo.id}`} title="Download PDF">
                      <Download className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => openEdit(wo)} data-testid={`edit-${wo.id}`}>
                      <FileText className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => remove(wo)} className="text-red-600" data-testid={`del-${wo.id}`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[720px] max-h-[90vh] overflow-y-auto" data-testid="work-order-dialog">
          <DialogHeader>
            <DialogTitle className="font-manrope">
              {editing ? `Edit ${editing.work_order_number}` : 'New Work Order'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Property *</Label>
                <Select value={form.property_id} onValueChange={(v) => setForm((p) => ({ ...p, property_id: v }))}>
                  <SelectTrigger data-testid="wo-property-select"><SelectValue placeholder="Pick property" /></SelectTrigger>
                  <SelectContent>
                    {properties?.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Assigned Worker (optional)</Label>
                <Select value={form.worker_id || 'none'} onValueChange={(v) => setForm((p) => ({ ...p, worker_id: v === 'none' ? '' : v }))}>
                  <SelectTrigger data-testid="wo-worker-select"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Unassigned</SelectItem>
                    {workers.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Client Name</Label>
                <Input value={form.client_name} onChange={(e) => setForm((p) => ({ ...p, client_name: e.target.value }))} data-testid="wo-client-name" />
              </div>
              <div className="space-y-1">
                <Label>Client Email</Label>
                <Input type="email" value={form.client_email} onChange={(e) => setForm((p) => ({ ...p, client_email: e.target.value }))} />
              </div>
            </div>

            <div className="space-y-1">
              <Label>Scope of Work *</Label>
              <Textarea
                rows={2}
                value={form.scope_of_work}
                onChange={(e) => setForm((p) => ({ ...p, scope_of_work: e.target.value }))}
                placeholder="Describe the job in a sentence or two"
                data-testid="wo-scope"
              />
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1">
                <Label>Expected Duration (days)</Label>
                <Input type="number" min="0" value={form.expected_duration_days}
                  onChange={(e) => setForm((p) => ({ ...p, expected_duration_days: e.target.value }))} />
              </div>
              <div className="space-y-1">
                <Label>Scheduled Start</Label>
                <Input type="date" value={form.scheduled_start}
                  onChange={(e) => setForm((p) => ({ ...p, scheduled_start: e.target.value }))} />
              </div>
              <div className="space-y-1">
                <Label>Scheduled End</Label>
                <Input type="date" value={form.scheduled_end}
                  onChange={(e) => setForm((p) => ({ ...p, scheduled_end: e.target.value }))} />
              </div>
            </div>

            {/* Line items */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Line Items</Label>
                <div className="flex gap-2">
                  <Button type="button" size="sm" variant="outline" onClick={() => addLine('labor')} data-testid="add-labor-line">
                    <Plus className="h-3 w-3 mr-1" /> Labor
                  </Button>
                  <Button type="button" size="sm" variant="outline" onClick={() => addLine('material')} data-testid="add-material-line">
                    <Plus className="h-3 w-3 mr-1" /> Material
                  </Button>
                </div>
              </div>
              <div className="space-y-2">
                {form.line_items.map((li, idx) => (
                  <LineRow
                    key={li._key || `li-legacy-${idx}`}
                    item={li}
                    idx={idx}
                    onChange={updateLine}
                    onRemove={removeLine}
                  />
                ))}
              </div>
            </div>

            <div className="grid grid-cols-4 gap-3">
              <div className="space-y-1">
                <Label>Tax %</Label>
                <Input type="number" min="0" step="0.1" value={form.tax_rate}
                  onChange={(e) => setForm((p) => ({ ...p, tax_rate: e.target.value }))} />
              </div>
              <div className="space-y-1">
                <Label>Labor Adj.</Label>
                <Input type="number" step="0.01" value={form.labor_adjustment}
                  onChange={(e) => setForm((p) => ({ ...p, labor_adjustment: e.target.value }))}
                  data-testid="wo-labor-adjustment"
                />
              </div>
              <div className="space-y-1">
                <Label>Range Min</Label>
                <Input type="number" min="0" step="0.01" value={form.cost_range_min}
                  onChange={(e) => setForm((p) => ({ ...p, cost_range_min: e.target.value }))} />
              </div>
              <div className="space-y-1">
                <Label>Range Max</Label>
                <Input type="number" min="0" step="0.01" value={form.cost_range_max}
                  onChange={(e) => setForm((p) => ({ ...p, cost_range_max: e.target.value }))} />
              </div>
            </div>

            <div className="space-y-1">
              <Label>Message to Client (optional)</Label>
              <Textarea rows={2} value={form.custom_message}
                onChange={(e) => setForm((p) => ({ ...p, custom_message: e.target.value }))} />
            </div>
            <div className="space-y-1">
              <Label>Terms &amp; Conditions (optional)</Label>
              <Textarea rows={2} value={form.terms}
                onChange={(e) => setForm((p) => ({ ...p, terms: e.target.value }))}
                placeholder="e.g. 50% deposit on acceptance, remainder due on completion" />
            </div>
            <div className="space-y-1">
              <Label>Internal Notes (optional)</Label>
              <Textarea rows={2} value={form.notes}
                onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))} />
            </div>

            <div className="space-y-1">
              <Label>Attached Photos</Label>
              <PhotoUploader
                kind="work_order_photo"
                onAdd={(url) => setForm((p) => ({ ...p, photos: [...p.photos, url] }))}
                testIdPrefix="wo-photo-uploader"
              />
              {form.photos.length > 0 && (
                <div className="flex gap-2 flex-wrap mt-2">
                  {form.photos.map((url, i) => (
                    <div key={`${url.slice(-20)}-${i}`} className="relative group">
                      <img src={url} alt="attached" className="w-16 h-16 object-cover rounded border" />
                      <button
                        type="button"
                        onClick={() => setForm((p) => ({ ...p, photos: p.photos.filter((_, x) => x !== i) }))}
                        className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={busy} className="bg-slate-900 hover:bg-slate-800" data-testid="save-wo-btn">
                {editing ? 'Save Changes' : 'Create Work Order'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WorkOrdersView;
